/**
 * UI State Machine for AppBouncer SDK
 * Orchestrates the entire verification flow across different modules
 */

import { i18n } from '../i18n';

// ============================================================
// Types
// ============================================================

export type VerificationMode = 'face_only' | 'liveness_only' | 'document_only' | 'full';

export type ScreenName =
  | 'initial' // Welcome/start screen
  | 'permissions' // Camera permission request
  | 'face_position' // Face positioning guide
  | 'liveness_check' // Liveness verification
  | 'liveness_action' // Performing specific action
  | 'liveness_complete' // Liveness passed
  | 'document_consent' // Permission modal for document/selfie storage
  | 'document_select' // Select document type/country
  | 'document_front' // Capture front of document
  | 'document_front_preview' // Review front capture
  | 'document_back' // Capture back of document
  | 'document_back_preview' // Review back capture
  | 'document_processing' // Uploading/processing documents
  | 'selfie_capture' // Take selfie with ID
  | 'processing' // Server verification in progress
  | 'success' // Verification successful
  | 'failure' // Verification failed
  | 'error' // System error
  | 'tips'; // Photo tips modal

/**
 * Backend method names that determine which verification steps are required
 */
export type VerificationMethod = 'liveness' | 'age' | 'ocr' | 'ocr_fallback';

export interface ScreenConfig {
  name: ScreenName;
  title: string;
  subtitle?: string;
  showCamera: boolean;
  showOverlay: boolean;
  showProgress: boolean;
  progressStep?: number;
  totalSteps?: number;
  canGoBack: boolean;
  actions: ScreenAction[];
}

export interface ScreenAction {
  id: string;
  label: string;
  type: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  handler: () => void | Promise<void>;
}

export interface UIStateConfig {
  /** Verification mode determines which screens are shown */
  mode: VerificationMode;
  /** Callback when screen changes */
  onScreenChange?: (screen: ScreenConfig, prevScreen?: ScreenName) => void;
  /** Callback when verification completes */
  onComplete?: (result: VerificationResult) => void;
  /** Callback on error */
  onError?: (error: Error, screen: ScreenName) => void;
  /** Callback for status messages */
  onStatusMessage?: (message: string, type: 'info' | 'success' | 'warning' | 'error') => void;
  /** Callback when action is required (blink, turn head, etc) */
  onActionRequired?: (action: string, instruction: string) => void;
  /** Custom screen configurations (override defaults) */
  customScreens?: Partial<Record<ScreenName, Partial<ScreenConfig>>>;
}

export interface VerificationResult {
  success: boolean;
  sessionId?: string;
  livenessScore?: number;
  ageEstimate?: number;
  documentVerified?: boolean;
  faceMatch?: boolean;
  errors?: string[];
}

export interface UIStateData {
  /** Current screen */
  currentScreen: ScreenName;
  /** Screen history for back navigation */
  history: ScreenName[];
  /** Whether verification is in progress */
  isProcessing: boolean;
  /** Current progress (0-100) */
  progress: number;
  /** Current liveness action being performed */
  currentAction?: string;
  /** Liveness actions completed */
  actionsCompleted: string[];
  /** Document type selected */
  documentType?: string;
  /** Country selected */
  countryCode?: string;
  /** Front document captured */
  frontCaptured: boolean;
  /** Back document captured */
  backCaptured: boolean;
  /** Error message if any */
  errorMessage?: string;
  /** Session token for verification */
  sessionToken?: string;
  /** Required verification methods from backend */
  requiredMethods: VerificationMethod[];
  /** Whether age verification passed (from ONNX model) */
  ageVerified: boolean;
  /** OCR extracted age from document */
  ocrAge: number;
  /** Estimated age from ONNX model */
  estimatedAge: number;
  /** Confidence of age estimation (0-1) */
  ageConfidence: number;
  /** Best image for upload (data URL) */
  bestImageForUpload: string | null;
  /** User consent for data collection (null = not asked) */
  consentGiven: boolean | null;
  /**
   * Minimum age required for verification.
   * Set from backend requirements response.
   */
  minAge: number;
}

// ============================================================
// Default Screen Configurations
// ============================================================

/**
 * Screen layout configuration (non-localized).
 */
interface ScreenLayout {
  showCamera: boolean;
  showOverlay: boolean;
  showProgress: boolean;
  progressStep?: number;
  totalSteps?: number;
  canGoBack: boolean;
}

/**
 * Static screen layout configurations.
 */
const SCREEN_LAYOUTS: Record<ScreenName, ScreenLayout> = {
  initial: { showCamera: false, showOverlay: false, showProgress: false, canGoBack: false },
  permissions: { showCamera: false, showOverlay: false, showProgress: false, canGoBack: true },
  face_position: {
    showCamera: true,
    showOverlay: true,
    showProgress: true,
    progressStep: 1,
    totalSteps: 4,
    canGoBack: true,
  },
  liveness_check: {
    showCamera: true,
    showOverlay: true,
    showProgress: true,
    progressStep: 2,
    totalSteps: 4,
    canGoBack: false,
  },
  liveness_action: {
    showCamera: true,
    showOverlay: true,
    showProgress: true,
    progressStep: 2,
    totalSteps: 4,
    canGoBack: false,
  },
  liveness_complete: {
    showCamera: true,
    showOverlay: true,
    showProgress: true,
    progressStep: 2,
    totalSteps: 4,
    canGoBack: false,
  },
  document_consent: {
    showCamera: false,
    showOverlay: false,
    showProgress: true,
    progressStep: 3,
    totalSteps: 4,
    canGoBack: false,
  },
  document_select: {
    showCamera: false,
    showOverlay: false,
    showProgress: true,
    progressStep: 3,
    totalSteps: 4,
    canGoBack: true,
  },
  document_front: {
    showCamera: true,
    showOverlay: true,
    showProgress: true,
    progressStep: 3,
    totalSteps: 4,
    canGoBack: true,
  },
  document_front_preview: {
    showCamera: false,
    showOverlay: false,
    showProgress: true,
    progressStep: 3,
    totalSteps: 4,
    canGoBack: true,
  },
  document_back: {
    showCamera: true,
    showOverlay: true,
    showProgress: true,
    progressStep: 3,
    totalSteps: 4,
    canGoBack: true,
  },
  document_back_preview: {
    showCamera: false,
    showOverlay: false,
    showProgress: true,
    progressStep: 3,
    totalSteps: 4,
    canGoBack: true,
  },
  document_processing: {
    showCamera: false,
    showOverlay: false,
    showProgress: true,
    progressStep: 3,
    totalSteps: 4,
    canGoBack: false,
  },
  selfie_capture: {
    showCamera: true,
    showOverlay: true,
    showProgress: true,
    progressStep: 4,
    totalSteps: 4,
    canGoBack: true,
  },
  processing: {
    showCamera: false,
    showOverlay: false,
    showProgress: true,
    progressStep: 4,
    totalSteps: 4,
    canGoBack: false,
  },
  success: { showCamera: false, showOverlay: false, showProgress: false, canGoBack: false },
  failure: { showCamera: false, showOverlay: false, showProgress: false, canGoBack: false },
  error: { showCamera: false, showOverlay: false, showProgress: false, canGoBack: true },
  tips: { showCamera: false, showOverlay: false, showProgress: false, canGoBack: true },
};

/**
 * Gets localized screen text (title and subtitle) for a given screen.
 */
function getLocalizedScreenText(screen: ScreenName): { title: string; subtitle: string } {
  const t = i18n.getTranslations();

  switch (screen) {
    case 'initial':
      return { title: t.screens.welcome.title, subtitle: t.screens.welcome.subtitle };
    case 'permissions':
      return { title: t.screens.permissions.title, subtitle: t.screens.permissions.description };
    case 'face_position':
      return { title: t.screens.facePosition.title, subtitle: t.screens.facePosition.hint };
    case 'liveness_check':
      return {
        title: t.screens.livenessCheck.title,
        subtitle: t.screens.livenessCheck.followInstructions,
      };
    case 'liveness_action':
      return { title: t.screens.livenessCheck.title, subtitle: '' }; // Dynamic based on action
    case 'liveness_complete':
      return { title: t.screens.livenessCheck.title, subtitle: t.screens.livenessCheck.complete };
    case 'document_consent':
      // Note: Need to add these keys to i18n translations
      return {
        title: t.screens.documentConsent?.title ?? 'Document Verification',
        subtitle: t.screens.documentConsent?.description ?? 'Additional verification required',
      };
    case 'document_select':
      return {
        title: t.screens.documentSelect.title,
        subtitle: t.screens.documentSelect.description,
      };
    case 'document_front':
      return {
        title: t.screens.documentCamera.frontSide,
        subtitle: t.screens.documentCamera.positionInFrame,
      };
    case 'document_front_preview':
      return {
        title: t.screens.documentPreview.frontPreview,
        subtitle: t.screens.documentPreview.checkDetails,
      };
    case 'document_back':
      return {
        title: t.screens.documentCamera.backSide,
        subtitle: t.screens.documentCamera.positionInFrame,
      };
    case 'document_back_preview':
      return {
        title: t.screens.documentPreview.backPreview,
        subtitle: t.screens.documentPreview.checkDetails,
      };
    case 'document_processing':
      return {
        title: t.screens.processing.title,
        subtitle: t.screens.processing.analyzingDocument,
      };
    case 'selfie_capture':
      return { title: t.screens.ageEstimation.title, subtitle: t.screens.ageEstimation.holdStill };
    case 'processing':
      return { title: t.screens.processing.title, subtitle: t.screens.processing.description };
    case 'success':
      return { title: t.screens.success.title, subtitle: t.screens.success.description };
    case 'failure':
      return { title: t.screens.failure.title, subtitle: t.screens.failure.description };
    case 'error':
      return { title: t.screens.error.title, subtitle: t.screens.error.description };
    case 'tips':
      return { title: t.document.tips.goodLighting, subtitle: t.document.tips.noGlare };
    default:
      return { title: screen, subtitle: '' };
  }
}

/**
 * Gets the default screen config with localized text.
 */
function getDefaultScreenConfig(screen: ScreenName): Omit<ScreenConfig, 'actions'> {
  const layout = SCREEN_LAYOUTS[screen];
  const text = getLocalizedScreenText(screen);

  return {
    name: screen,
    title: text.title,
    subtitle: text.subtitle,
    ...layout,
  };
}

// ============================================================
// Flow Definitions
// ============================================================

const FLOW_SCREENS: Record<VerificationMode, ScreenName[]> = {
  face_only: ['initial', 'permissions', 'face_position', 'processing', 'success'],
  liveness_only: [
    'initial',
    'permissions',
    'face_position',
    'liveness_check',
    'liveness_complete',
    'processing',
    'success',
  ],
  document_only: [
    'initial',
    'permissions',
    'document_select',
    'document_front',
    'document_front_preview',
    'document_back',
    'document_back_preview',
    'document_processing',
    'success',
  ],
  full: [
    'initial',
    'permissions',
    'face_position',
    'liveness_check',
    'liveness_complete',
    'document_select',
    'document_front',
    'document_front_preview',
    'document_back',
    'document_back_preview',
    'selfie_capture',
    'processing',
    'success',
  ],
};

// ============================================================
// State Machine Class
// ============================================================

export class UIStateMachine {
  private config: UIStateConfig;
  private state: UIStateData;
  private flowScreens: ScreenName[];

  constructor(config: UIStateConfig) {
    this.config = config;
    this.flowScreens = FLOW_SCREENS[config.mode];
    this.state = this.createInitialState();
  }

  private createInitialState(): UIStateData {
    return {
      currentScreen: 'initial',
      history: [],
      isProcessing: false,
      progress: 0,
      actionsCompleted: [],
      frontCaptured: false,
      backCaptured: false,
      requiredMethods: [],
      ageVerified: false,
      ocrAge: 0,
      estimatedAge: 0,
      ageConfidence: 0,
      bestImageForUpload: null,
      consentGiven: null,
      minAge: 18, // Default, overridden by backend
    };
  }

  // ============================================================
  // Public API
  // ============================================================

  /**
   * Get current screen configuration
   */
  getCurrentScreen(): ScreenConfig {
    return this.buildScreenConfig(this.state.currentScreen);
  }

  /**
   * Get current state data
   */
  getState(): Readonly<UIStateData> {
    return { ...this.state };
  }

  /**
   * Start the verification flow
   */
  start(): void {
    this.transitionTo('initial');
  }

  /**
   * Navigate to next screen in the flow
   */
  next(): void {
    const currentIndex = this.flowScreens.indexOf(this.state.currentScreen);
    if (currentIndex >= 0 && currentIndex < this.flowScreens.length - 1) {
      this.transitionTo(this.flowScreens[currentIndex + 1]);
    }
  }

  /**
   * Go back to previous screen
   */
  back(): void {
    if (this.state.history.length > 0) {
      const prevScreen = this.state.history.pop()!;
      this.transitionTo(prevScreen, false);
    }
  }

  /**
   * Navigate to a specific screen
   */
  goTo(screen: ScreenName): void {
    this.transitionTo(screen);
  }

  /**
   * Skip OCR screens and go directly to processing.
   * Used when ocr_fallback is enabled but risk conditions aren't met.
   */
  skipToProcessing(): void {
    this.transitionTo('processing');
  }

  /**
   * Check if the next screen in the flow is an OCR-related screen.
   * Used to determine if OCR fallback logic should be applied.
   */
  isNextScreenOcr(): boolean {
    const currentIndex = this.flowScreens.indexOf(this.state.currentScreen);
    if (currentIndex >= 0 && currentIndex < this.flowScreens.length - 1) {
      const nextScreen = this.flowScreens[currentIndex + 1];
      const ocrScreens: ScreenName[] = [
        'document_consent', // Consent gateway to OCR flow
        'document_select',
        'document_front',
        'document_front_preview',
        'document_back',
        'document_back_preview',
        'document_processing',
      ];
      return ocrScreens.includes(nextScreen);
    }
    return false;
  }

  /**
   * Reset state machine to initial state
   */
  reset(): void {
    this.state = this.createInitialState();
    this.transitionTo('initial');
  }

  /**
   * Set processing state
   */
  setProcessing(isProcessing: boolean, progress: number = 0): void {
    this.state.isProcessing = isProcessing;
    this.state.progress = progress;
  }

  /**
   * Update progress
   */
  setProgress(progress: number): void {
    this.state.progress = Math.min(100, Math.max(0, progress));
  }

  /**
   * Set current liveness action
   */
  setCurrentAction(action: string | undefined): void {
    this.state.currentAction = action;
  }

  /**
   * Mark a liveness action as completed
   */
  markActionCompleted(action: string): void {
    if (!this.state.actionsCompleted.includes(action)) {
      this.state.actionsCompleted.push(action);
    }
  }

  /**
   * Set document selection
   */
  setDocumentSelection(documentType: string, countryCode: string): void {
    this.state.documentType = documentType;
    this.state.countryCode = countryCode;
  }

  /**
   * Mark document side as captured
   */
  markDocumentCaptured(side: 'front' | 'back'): void {
    if (side === 'front') {
      this.state.frontCaptured = true;
    } else {
      this.state.backCaptured = true;
    }
  }

  /**
   * Set session token
   */
  setSessionToken(token: string): void {
    this.state.sessionToken = token;
  }

  /**
   * Set whether age verification passed (from ONNX model)
   */
  setAgeVerified(verified: boolean): void {
    this.state.ageVerified = verified;
  }

  /**
   * Set OCR extracted age from document
   */
  setOcrAge(age: number): void {
    this.state.ocrAge = age;
  }

  /**
   * Set estimated age from ONNX model
   */
  setEstimatedAge(age: number): void {
    this.state.estimatedAge = age;
  }

  /**
   * Set age estimation confidence (0-1)
   */
  setAgeConfidence(confidence: number): void {
    this.state.ageConfidence = confidence;
  }

  /**
   * Set minimum age requirement (from backend)
   */
  setMinAge(minAge: number): void {
    this.state.minAge = minAge;
  }

  /**
   * Check if age is verified with high confidence (99%+)
   * This is the threshold at which OCR can be skipped.
   */
  isAgeVerifiedWithHighConfidence(): boolean {
    const AGE_CONFIDENCE_THRESHOLD = 0.99;
    return (
      this.state.ageVerified &&
      this.state.ageConfidence >= AGE_CONFIDENCE_THRESHOLD &&
      this.state.estimatedAge >= this.state.minAge
    );
  }

  /**
   * Set best image for upload
   */
  setBestImage(imageDataUrl: string | null): void {
    this.state.bestImageForUpload = imageDataUrl;
  }

  /**
   * Set user consent for data collection
   */
  setConsentGiven(consent: boolean | null): void {
    this.state.consentGiven = consent;
  }

  /**
   * Build and set the flow from backend verification methods.
   * This is the core method that enables dynamic wizard building.
   *
   * @param methods - Array of method names from /v1/requirements/check
   * @example
   * // Backend returns: { methods: ['liveness', 'age', 'ocr'] }
   * stateMachine.setFlowFromMethods(['liveness', 'age', 'ocr']);
   */
  setFlowFromMethods(methods: VerificationMethod[]): void {
    this.state.requiredMethods = methods;
    this.flowScreens = this.buildFlowFromMethods(methods);

    // Recalculate total steps based on new flow
    // Exclude initial, permissions, processing, success, failure, error, tips
    const progressScreens = this.flowScreens.filter(
      (s) =>
        !['initial', 'permissions', 'processing', 'success', 'failure', 'error', 'tips'].includes(s)
    );

    // Update progress step totals for all screens
    this.updateProgressSteps(progressScreens.length);
  }

  /**
   * Build screen flow from verification methods.
   * Maps backend method names to SDK screen sequences.
   *
   * Note: document_consent is added before OCR screens, but the actual
   * navigation to it is controlled by goNext() based on age confidence.
   * If age is verified with 99%+ confidence, we skip directly to processing.
   */
  private buildFlowFromMethods(methods: VerificationMethod[]): ScreenName[] {
    const screens: ScreenName[] = ['initial', 'permissions'];

    // Add liveness/age screens if either is required
    // Age is captured during liveness, so we only need liveness screens
    if (methods.includes('liveness') || methods.includes('age')) {
      screens.push('face_position', 'liveness_check', 'liveness_complete');
    }

    // Add OCR screens only if 'ocr' is required (always-required document verification).
    // For 'ocr_fallback', document screens are NOT in the static flow — they are
    // navigated to dynamically via goToScreen() in handleAgeContinue() only when
    // age verification fails. This keeps the progress indicator and step count accurate.
    if (methods.includes('ocr')) {
      screens.push(
        'document_consent',
        'document_select',
        'document_front',
        'document_front_preview',
        'document_back',
        'document_back_preview',
        'document_processing'
      );
    }

    // Add final processing and success screens
    screens.push('processing', 'success');

    return screens;
  }

  /**
   * Update total steps count for all screens based on dynamic flow
   */
  private updateProgressSteps(_totalSteps: number): void {
    // This updates the step indicators to reflect the dynamic flow
    // The actual progressStep for each screen is calculated during buildScreenConfig
    this.state.progress = 0;
  }

  /**
   * Get current step number for progress display
   */
  getCurrentStepNumber(): number {
    const progressScreens = this.flowScreens.filter(
      (s) =>
        !['initial', 'permissions', 'processing', 'success', 'failure', 'error', 'tips'].includes(s)
    );
    const index = progressScreens.indexOf(this.state.currentScreen);
    return index >= 0 ? index + 1 : 0;
  }

  /**
   * Get total number of steps for progress display
   */
  getTotalSteps(): number {
    return this.flowScreens.filter(
      (s) =>
        !['initial', 'permissions', 'processing', 'success', 'failure', 'error', 'tips'].includes(s)
    ).length;
  }

  /**
   * Check if a specific method is required
   */
  isMethodRequired(method: VerificationMethod): boolean {
    return this.state.requiredMethods.includes(method);
  }

  /**
   * Get the current flow screens (useful for debugging)
   */
  getFlowScreens(): ScreenName[] {
    return [...this.flowScreens];
  }

  /**
   * Handle error
   */
  handleError(error: Error): void {
    this.state.errorMessage = error.message;
    this.config.onError?.(error, this.state.currentScreen);
    this.transitionTo('error');
  }

  /**
   * Complete verification with result
   */
  complete(result: VerificationResult): void {
    if (result.success) {
      this.transitionTo('success');
    } else {
      this.state.errorMessage = result.errors?.join(', ') || 'Verification failed';
      this.transitionTo('failure');
    }
    this.config.onComplete?.(result);
  }

  /**
   * Show tips screen
   */
  showTips(): void {
    this.transitionTo('tips');
  }

  /**
   * Emit status message
   */
  emitStatus(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    this.config.onStatusMessage?.(message, type);
  }

  // ============================================================
  // Private Methods
  // ============================================================

  private transitionTo(screen: ScreenName, addToHistory: boolean = true): void {
    const prevScreen = this.state.currentScreen;

    // Add to history if moving forward
    if (addToHistory && prevScreen !== screen) {
      this.state.history.push(prevScreen);
    }

    this.state.currentScreen = screen;

    const screenConfig = this.buildScreenConfig(screen);
    this.config.onScreenChange?.(screenConfig, prevScreen);
  }

  private buildScreenConfig(screen: ScreenName): ScreenConfig {
    const baseConfig = getDefaultScreenConfig(screen);
    const customConfig = this.config.customScreens?.[screen];

    // Build actions based on current screen and state
    const actions = this.buildScreenActions(screen);

    // Apply subtitle override for liveness actions
    let subtitle = customConfig?.subtitle ?? baseConfig.subtitle;
    if (screen === 'liveness_action' && this.state.currentAction) {
      subtitle = this.getActionInstruction(this.state.currentAction);
    }
    if (screen === 'error' && this.state.errorMessage) {
      subtitle = this.state.errorMessage;
    }

    return {
      ...baseConfig,
      ...customConfig,
      subtitle,
      actions,
    };
  }

  private buildScreenActions(screen: ScreenName): ScreenAction[] {
    const actions: ScreenAction[] = [];
    const t = i18n.getTranslations();

    switch (screen) {
      case 'initial':
        actions.push({
          id: 'start',
          label: t.screens.welcome.startButton,
          type: 'primary',
          handler: () => this.next(),
        });
        break;

      case 'permissions':
        actions.push({
          id: 'allow',
          label: t.screens.permissions.allowButton,
          type: 'primary',
          handler: () => this.next(),
        });
        break;

      case 'face_position':
        actions.push({
          id: 'tips',
          label: t.document.tips.goodLighting,
          type: 'secondary',
          handler: () => this.showTips(),
        });
        break;

      case 'document_consent':
        // Actions handled externally by DocumentConsentModal component
        // The modal shows Allow/Decline buttons and calls goTo() directly
        actions.push(
          {
            id: 'allow',
            label: t.screens.documentConsent?.allowButton ?? 'Allow',
            type: 'primary',
            handler: () => {
              this.setConsentGiven(true);
              this.next();
            },
          },
          {
            id: 'decline',
            label: t.screens.documentConsent?.declineButton ?? 'Decline',
            type: 'secondary',
            handler: () => {
              this.setConsentGiven(false);
              this.goTo('failure');
            },
          }
        );
        break;

      case 'document_select':
        actions.push({
          id: 'continue',
          label: t.ui.continue,
          type: 'primary',
          disabled: !this.state.documentType || !this.state.countryCode,
          handler: () => this.next(),
        });
        break;

      case 'document_front':
      case 'document_back':
        actions.push(
          {
            id: 'capture',
            label: t.screens.documentCamera.capturePhoto,
            type: 'primary',
            handler: () => this.next(),
          },
          {
            id: 'tips',
            label: t.document.tips.goodLighting,
            type: 'secondary',
            handler: () => this.showTips(),
          }
        );
        break;

      case 'document_front_preview':
      case 'document_back_preview':
        actions.push(
          {
            id: 'confirm',
            label: t.screens.documentPreview.usePhoto,
            type: 'primary',
            handler: () => this.next(),
          },
          {
            id: 'retake',
            label: t.screens.documentPreview.retakePhoto,
            type: 'secondary',
            handler: () => this.back(),
          }
        );
        break;

      case 'selfie_capture':
        actions.push({
          id: 'capture',
          label: t.screens.documentCamera.capturePhoto,
          type: 'primary',
          handler: () => this.next(),
        });
        break;

      case 'success':
        actions.push({
          id: 'done',
          label: t.screens.success.done,
          type: 'primary',
          handler: () => {}, // External handling
        });
        break;

      case 'failure':
        actions.push(
          {
            id: 'retry',
            label: t.screens.failure.tryAgainButton,
            type: 'primary',
            handler: () => this.reset(),
          },
          {
            id: 'cancel',
            label: t.ui.cancel,
            type: 'secondary',
            handler: () => {}, // External handling
          }
        );
        break;

      case 'error':
        actions.push(
          {
            id: 'retry',
            label: t.ui.retry,
            type: 'primary',
            handler: () => this.back(),
          },
          {
            id: 'cancel',
            label: t.ui.cancel,
            type: 'secondary',
            handler: () => {}, // External handling
          }
        );
        break;

      case 'tips':
        actions.push({
          id: 'close',
          label: t.ui.continue,
          type: 'primary',
          handler: () => this.back(),
        });
        break;
    }

    return actions;
  }

  private getActionInstruction(action: string): string {
    const t = i18n.getTranslations();

    // Map action names to i18n liveness keys
    const actionI18nMap: Record<string, keyof typeof t.liveness> = {
      BLINK: 'blink',
      DOUBLE_BLINK: 'blink',
      LOOK_LEFT: 'lookLeft',
      LOOK_RIGHT: 'lookRight',
      LOOK_UP: 'lookUp',
      LOOK_DOWN: 'lookDown',
      SMILE: 'smile',
      OPEN_MOUTH: 'openMouth',
      RAISE_EYEBROWS: 'raiseBrows',
      RAISE_BROWS: 'raiseBrows',
      TURN_HEAD_LEFT: 'lookLeft',
      TURN_HEAD_RIGHT: 'lookRight',
      NOD: 'lookDown',
    };

    const i18nKey = actionI18nMap[action];
    if (i18nKey) {
      const actionText = t.liveness[i18nKey];
      if (actionText && typeof actionText === 'object' && 'hint' in actionText) {
        return actionText.hint;
      }
    }

    return action;
  }
}

// ============================================================
// Screen Component Helpers
// ============================================================

/**
 * Get step indicator data for progress display
 */
export function getStepIndicator(
  currentStep: number,
  totalSteps: number
): { steps: { number: number; completed: boolean; current: boolean }[] } {
  const steps = [];
  for (let i = 1; i <= totalSteps; i++) {
    steps.push({
      number: i,
      completed: i < currentStep,
      current: i === currentStep,
    });
  }
  return { steps };
}

/**
 * Photo tips content (localized)
 */
export function getPhotoTips(): {
  face: { icon: string; text: string }[];
  document: { icon: string; text: string }[];
} {
  const t = i18n.getTranslations();
  return {
    face: [
      { icon: '💡', text: t.document.tips.goodLighting },
      { icon: '📱', text: t.screens.facePosition.hint },
      { icon: '🚫', text: t.face.centerFace },
      { icon: '😐', text: t.face.holdStill },
      { icon: '🔲', text: t.face.centerInFrame },
    ],
    document: [
      { icon: '📄', text: t.document.tips.flatSurface },
      { icon: '💡', text: t.document.tips.goodLighting },
      { icon: '📐', text: t.document.tips.allCorners },
      { icon: '🔍', text: t.screens.documentPreview.checkDetails },
      { icon: '✨', text: t.document.tips.noGlare },
    ],
  };
}

// Legacy export for backwards compatibility
export const PHOTO_TIPS = {
  get face() {
    return getPhotoTips().face;
  },
  get document() {
    return getPhotoTips().document;
  },
};

/**
 * Document type options (localized)
 */
export function getDocumentTypes(): { id: string; label: string; icon: string }[] {
  const t = i18n.getTranslations();
  return [
    { id: 'government_id', label: t.screens.documentSelect.idCard, icon: '🪪' },
    { id: 'driver_license', label: t.screens.documentSelect.driversLicense, icon: '🚗' },
    { id: 'passport', label: t.screens.documentSelect.passport, icon: '📕' },
    { id: 'national_id', label: t.screens.documentSelect.idCard, icon: '🆔' },
  ];
}

// Legacy export for backwards compatibility
export const DOCUMENT_TYPES = {
  get length() {
    return getDocumentTypes().length;
  },
  [Symbol.iterator]() {
    return getDocumentTypes()[Symbol.iterator]();
  },
  map<T>(fn: (item: { id: string; label: string; icon: string }, index: number) => T): T[] {
    return getDocumentTypes().map(fn);
  },
  forEach(fn: (item: { id: string; label: string; icon: string }, index: number) => void): void {
    getDocumentTypes().forEach(fn);
  },
};

/**
 * Common error messages (localized)
 */
export function getErrorMessages(): Record<string, string> {
  const t = i18n.getTranslations();
  return {
    camera_denied: t.errors.cameraPermissionDenied,
    camera_unavailable: t.errors.cameraNotFound,
    no_face_detected: t.face.noFace,
    multiple_faces: t.face.centerFace,
    poor_lighting: t.face.poorLighting,
    face_too_far: t.face.tooFar,
    face_too_close: t.face.tooClose,
    liveness_failed: t.screens.livenessCheck.failed,
    document_unreadable: t.screens.documentPreview.retakePhoto,
    network_error: t.errors.networkError,
    server_error: t.errors.unknownError,
    session_expired: t.errors.sessionExpired,
    verification_failed: t.errors.verificationFailed,
  };
}

// Legacy export for backwards compatibility
export const ERROR_MESSAGES = new Proxy({} as Record<string, string>, {
  get(_target, prop: string) {
    return getErrorMessages()[prop] || prop;
  },
});

export type ErrorCode =
  | 'camera_denied'
  | 'camera_unavailable'
  | 'no_face_detected'
  | 'multiple_faces'
  | 'poor_lighting'
  | 'face_too_far'
  | 'face_too_close'
  | 'liveness_failed'
  | 'document_unreadable'
  | 'network_error'
  | 'server_error'
  | 'session_expired'
  | 'verification_failed';

/**
 * Get user-friendly error message (localized)
 */
export function getErrorMessage(code: ErrorCode | string): string {
  const messages = getErrorMessages();
  return messages[code] || code;
}
